// Página de planos da versão web (AC-258): quem entra sem assinatura não cai no paywall de dentro do
// app, e sim numa página no visual do site — os dois planos lado a lado (o mensal em destaque, ou o
// plano escolhido no site), cada um com o botão que leva direto ao Stripe Checkout. No celular, para
// quem nunca assinou, a faixa vermelha no topo, o selo com o check de "aplicado" e o preço cheio
// riscado ao lado da 1ª cobrança com o cupom (AC-253). O app desktop continua com o Paywall.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { annualDiscount, annualPerMonth, currencyFor, guaranteeDays, PLANS, priceLabel, specPricing, type Plan, type Pricing } from '../domain/access';
import { discountedPrice, mobileOfferFor } from '../domain/mobileOffer';
import { browserStorage } from '../platform/adAttribution';
import { chosenPlan } from '../platform/entryParams';
import { useStore } from '../state/AppStore';
import { useServices } from '../state/services';
import { useAccess, useSubscriptionPolling } from '../state/useSubscription';
import { BeeField } from './auth/BeeField';
import { BeeLogo, Icon } from './Icon';
import { cls } from './NewTaskForm';
import { PromoBar } from './PromoBar';

function systemLocales(): readonly string[] {
  if (typeof navigator === 'undefined') return ['en-US'];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

const FEATURES = ['float', 'timer', 'pomodoro', 'sync'] as const;

export function PlansPage({ onBack }: { onBack(): void }) {
  const { t } = useTranslation();
  const store = useStore();
  const services = useServices();
  const access = useAccess();
  useSubscriptionPolling(true);
  const [busy, setBusy] = useState<Plan | null>(null);
  // em destaque: o plano escolhido no site; sem escolha, o mensal (foco do usuário em 03/11/2026)
  const [featured] = useState<Plan>(() => chosenPlan(browserStorage()) ?? 'monthly');
  const deviceCurrency = currencyFor(systemLocales());
  const [pricing, setPricing] = useState<Pricing | null>(null);
  useEffect(() => {
    let alive = true;
    store.backend.subscription
      .prices(deviceCurrency)
      .then((p) => alive && setPricing(p))
      .catch(() => alive && setPricing(specPricing(deviceCurrency)));
    return () => {
      alive = false;
    };
  }, [store, deviceCurrency]);
  const offer = mobileOfferFor(pricing?.mobileOffer, typeof navigator === 'undefined' ? '' : navigator.userAgent, access.neverSubscribed);

  const subscribe = async (plan: Plan) => {
    setBusy(plan);
    try {
      const url = await store.backend.subscription.checkoutUrl(plan);
      await services.openExternal(url);
    } catch {
      store.toast('errors.checkout');
    } finally {
      setBusy(null);
    }
  };

  const card = (plan: Plan) => {
    const annual = plan === 'annual';
    const full = pricing ? (annual ? pricing.annual : pricing.monthly) : 0;
    const unit = annual ? t('plans.perYear') : t('plans.perMonth');
    const strong = plan === featured;
    return (
      <article key={plan} className={cls('plan-card', strong && 'plan-card--featured')} data-testid={`plan-${plan}`}>
        <h2 className="plan-card__name">{t(`paywall.plan.${plan}`)}</h2>
        {pricing ? (
          offer > 0 ? (
            <p className="plan-card__price">
              <s className="plan-card__was" data-testid="price-was">
                {priceLabel(full, pricing.currency)}
              </s>
              <strong data-testid="price">{priceLabel(discountedPrice(full, offer), pricing.currency)}</strong>
              <span>{annual ? t('paywall.firstYear') : t('paywall.firstMonth')}</span>
            </p>
          ) : (
            <p className="plan-card__price">
              <strong data-testid="price">{priceLabel(full, pricing.currency)}</strong>
              <span>{unit}</span>
            </p>
          )
        ) : (
          <p className="plan-card__price plan-card__price--loading" aria-busy="true" aria-label={t('common.loading')} />
        )}
        <p className="plan-card__badges">
          {annual && pricing && annualDiscount(pricing) > 0 && <span className="plan-card__tag">{t('plans.save', { percent: annualDiscount(pricing) })}</span>}
          {offer > 0 && (
            <span className="plan-card__tag plan-card__tag--offer" data-testid="mobile-offer">
              <Icon name="check" size={14} />
              {t('paywall.mobileTag', { percent: offer })}
            </span>
          )}
        </p>
        <p className="plan-card__then">
          {annual && pricing ? t('paywall.annualPerMonth', { price: priceLabel(annualPerMonth(pricing), pricing.currency) }) : t('plans.noCommitment')}
        </p>
        {offer > 0 && pricing && (
          <p className="plan-card__then" data-testid="price-then">
            {t('plans.then', { price: `${priceLabel(full, pricing.currency)} ${unit}` })}
          </p>
        )}
        <ul className="plan-card__items">
          {FEATURES.map((k) => (
            <li key={k}>
              <Icon name="check" size={16} />
              {t(`paywall.features.${k}`)}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={cls('plan-card__action', strong ? 'plan-card__action--honey' : 'plan-card__action--ghost')}
          disabled={busy !== null || !store.get().online}
          onClick={() => void subscribe(plan)}
        >
          {annual ? t('plans.startAnnual') : t('plans.startMonthly')}
        </button>
      </article>
    );
  };

  return (
    <main className={cls('plans-page', offer > 0 && 'plans-page--promo')} data-testid="plans-page">
      {offer > 0 && <PromoBar text={t('paywall.promoBar', { percent: offer })} />}
      <BeeField />
      <header className="plans-page__head">
        <span className="plans-page__mark">
          <BeeLogo size={40} />
          <span>{t('app.name')}</span>
        </span>
        <button type="button" className="plans-page__link" onClick={() => void store.backend.auth.signOut()}>
          {t('settings.signOut')}
        </button>
      </header>
      <section className="plans-page__body">
        <h1 className="plans-page__title">{access.subscriptionEnded ? t('paywall.endedTitle') : t('plans.title')}</h1>
        <p className="plans-page__lead">{access.subscriptionEnded ? t('paywall.endedSub') : t('plans.lead')}</p>
        <div className="plans-page__grid">{PLANS.map(card)}</div>
        {pricing && (
          <p className="plans-page__trust" data-testid="guarantee">
            <span>
              <Icon name="check" size={14} />
              {t('paywall.guarantee', { count: guaranteeDays(pricing.currency) })}
            </span>
            <span>
              <Icon name="lock" size={14} />
              {t('paywall.secure')}
            </span>
          </p>
        )}
        <button type="button" className="plans-page__link" onClick={onBack}>
          {t('paywall.seeTasks')}
        </button>
      </section>
    </main>
  );
}
